import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ASPECT_RATIO_OPTIONS, FORMAT_PRESET_GROUPS } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';
import PanelBody from '../../components/panel-body/PanelBody';
import PanelSection from '../../components/panel-section/PanelSection';
import CustomSize from './components/custom-size/CustomSize';
import FormatPresetRow from './components/format-preset-row/FormatPresetRow';
import { CUSTOM_SIZE_ID, GROUP_ICONS, RATIO_LABEL_KEYS } from './constants';

import scss from './FormatsPanel.module.scss';

function FormatsPanel() {
  const { t } = useTranslation('mediaStudio');
  const { config, editor } = useMediaStudio();
  const { activeFormatId } = editor;
  const presets = config.formatPresets;
  const outputSize = editor.outputSize || { width: editor.width, height: editor.height };

  const cropToSize = (formatId, width, height) =>
    editor.startFormatCrop(`${width}:${height}`, formatId, { width, height });

  const grouped = useMemo(() => {
    return FORMAT_PRESET_GROUPS.map((group) => ({
      ...group,
      items: presets.filter((preset) => preset.group === group.id),
    })).filter((group) => group.items.length);
  }, [presets]);

  return (
    <PanelBody>
      <PanelSection title={t('formatCustomSize')}>
        <CustomSize
          outputSize={outputSize}
          active={activeFormatId === CUSTOM_SIZE_ID}
          onApply={(width, height) => cropToSize(CUSTOM_SIZE_ID, width, height)}
        />
      </PanelSection>

      <PanelSection title={t('formatsRatio')}>
        <div className={scss.ratios}>
          {ASPECT_RATIO_OPTIONS.map((ratio) => (
            <button
              key={ratio}
              type='button'
              className={clsx(scss.ratio, activeFormatId === ratio && scss.ratioActive)}
              aria-pressed={activeFormatId === ratio}
              onClick={() => editor.startFormatCrop(ratio, ratio)}>
              {t(RATIO_LABEL_KEYS[ratio])}
            </button>
          ))}
        </div>
      </PanelSection>

      {grouped.map((group) => (
        <PanelSection key={group.id} title={t(group.labelKey)} icon={GROUP_ICONS[group.id]}>
            <ul className={scss.list}>
              {group.items.map((preset) => (
                <li key={preset.id}>
                  <FormatPresetRow
                    preset={preset}
                    isActive={activeFormatId === preset.id}
                    onSelect={({ id, width, height }) => cropToSize(id, width, height)}
                  />
                </li>
              ))}
          </ul>
        </PanelSection>
      ))}
    </PanelBody>
  );
}

export default FormatsPanel;
